<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PrimeVue
import { default as PDataTable } from 'primevue/datatable'
import { default as PColumn } from 'primevue/column'
import { default as PTag } from 'primevue/tag'
import { default as PMessage } from 'primevue/message'

// VUE, VUE ROUTER ETC
import { useRouter } from 'vue-router'

// Custom components and types
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import CQuizVersionTag from '@/presentation/components/quiz/CQuizVersionTag.vue'
import type { ReferentialTask } from '@/core/domain/referential/task/ReferentialTask.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'

const router = useRouter()

const props = defineProps<{
  tasks: ReferentialTask[]
  learningCourseId: number
}>()

function editQuiz(learningQuiz: Quiz, taskId: number) {
  router.push({
    name: 'learning-course-update-quiz',
    params: { courseId: props.learningCourseId, quizId: learningQuiz.id },
    query: { taskId: taskId, quizType: learningQuiz.type }
  })
}

function editQuizWithVersion(learningQuiz: Quiz, taskId: number, versionId: number) {
  router.push({
    name: 'learning-course-update-quiz',
    params: { courseId: props.learningCourseId, quizId: learningQuiz.id },
    query: { taskId: taskId, quizType: learningQuiz.type, version: versionId }
  })
}

function getQuizCompletion(quiz: Quiz) {
  const numberOfQuestions = quiz.activeVersion?.questions.length || 0
  let text
  let color: string

  if (numberOfQuestions === 0) {
    text = 'Quiz vide'
    color = 'secondary'
  } else {
    text = `${numberOfQuestions} question${numberOfQuestions > 1 ? 's' : ''}`
    color = 'warning'
  }

  if (numberOfQuestions > 4) color = 'success'

  return {
    text,
    color
  }
}
</script>

<template>
  <c-base-block title="Liste des tâches" class="col-span-12 w-full">
    <p-data-table :value="tasks">
      <p-column field="title" header="Tâche"></p-column>

      <p-column field="description" header="Diagnostic T1">
        <template #body="{ data }: { data: ReferentialTask }">
          <div v-if="data.quizStart" class="flex items-start gap-2">
            <c-quiz-version-tag
              v-if="data.quizStart.activeVersion"
              :version="data.quizStart.activeVersion"
              severity="success"
              clickable
              show-question-count
              @click="editQuizWithVersion(data.quizStart, data.id, data.quizStart.activeVersion.id)"
            />

            <c-quiz-version-tag
              v-if="data.quizStart.latestVersion?.status === LearningStatus.Values.DRAFT"
              :version="data.quizStart.latestVersion"
              severity="warn"
              clickable
              show-question-count
              @click="editQuizWithVersion(data.quizStart, data.id, data.quizStart.latestVersion.id)"
            />
          </div>
          <p-message v-else severity="warning" icon="pi pi-info-circle" class="w-full">
            Aucun quiz n'a été trouvé.
          </p-message>
        </template>
      </p-column>

      <p-column field="description" header="Diagnostic T2">
        <template #body="{ data }: { data: ReferentialTask }">
          <div v-if="data.quizEnd" class="flex items-start gap-2">
            <c-quiz-version-tag
              v-if="data.quizEnd.activeVersion"
              :version="data.quizEnd.activeVersion"
              severity="success"
              clickable
              show-question-count
              @click="editQuizWithVersion(data.quizEnd, data.id, data.quizEnd.activeVersion.id)"
            />

            <c-quiz-version-tag
              v-if="data.quizEnd.latestVersion?.status === LearningStatus.Values.DRAFT"
              :version="data.quizEnd.latestVersion"
              severity="warn"
              clickable
              show-question-count
              @click="editQuizWithVersion(data.quizEnd, data.id, data.quizEnd.latestVersion.id)"
            />
          </div>
          <p-message v-else severity="warning" icon="pi pi-info-circle" class="w-full">
            Aucun quiz n'a été trouvé.
          </p-message>
        </template>
      </p-column>
    </p-data-table>
  </c-base-block>
</template>
